'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';

import { createPost } from '@dh/api/posts';
import { useBreakpoint, useToggle, useUser } from '@dh/hooks';
import { getLogger } from '@dh/logger/logger';
import { Column } from '@dh/styles';
import {
    FileToUpload,
    FormInput,
    getBreakpointValue,
    MixedMedia,
    Post,
    PostUpdate,
    RawPost
} from '@dh/types';
import {
    FileType,
    getMixedMediaFileSize,
    isFileToUpload,
    isLinkToUpload,
    mixedMediaIsLink,
    sortMediaUpdates
} from '@dh/utils';

import { DHTextField, ErrorDialog, FileUpload, LoadingOverlay, ModalDialog } from '../../..';

const logger = getLogger('postLogger');

export enum PostDialogMode {
    Create = 'create',
    Edit = 'edit'
}

interface BaseProps {
    onClose: () => void;
    onSuccess: () => void;
}

interface EditProps {
    post: Post;
}

interface GroupProps {
    groupId: string;
}

interface ProjectProps {
    projectId: string;
}

type Props = BaseProps &
    Partial<EditProps & GroupProps & ProjectProps> & {
        mode: PostDialogMode;
    };

export const ProjectPostCreationModal = (props: BaseProps & ProjectProps) => (
    <ProjectDetailsCoWorkCreateEditPostModal {...props} mode={PostDialogMode.Create} />
);

export const GroupPostCreationModal = (props: BaseProps & GroupProps) => (
    <ProjectDetailsCoWorkCreateEditPostModal {...props} mode={PostDialogMode.Create} />
);

export const PostEditModal = (props: BaseProps & EditProps) => (
    <ProjectDetailsCoWorkCreateEditPostModal {...props} mode={PostDialogMode.Edit} />
);

const ProjectDetailsCoWorkCreateEditPostModal = ({
    mode,
    projectId,
    groupId,
    post,
    onClose,
    onSuccess
}: Props) => {
    const t = useTranslations('details');
    const t_dialog = useTranslations('dialog');

    const breakpoint = useBreakpoint();
    const { user } = useUser();

    const [title, setTitle] = useState<FormInput<string>>({ value: post?.title ?? '' });
    const [text, setText] = useState<FormInput<string>>({ value: post?.text ?? '' });
    const [uploads, setUploads] = useState<FormInput<MixedMedia[]>>({
        value: post?.uploads ?? []
    });
    const [isLoading, setIsLoading] = useToggle();
    const [hasError, setHasError] = useState<boolean>(false);

    const isEditMode = mode === PostDialogMode.Edit;

    const onCreate = async () => {
        if (user === undefined || (projectId === undefined && groupId === undefined)) {
            logger.error('Could not create Post. Missing user, project or group id!');
            return;
        }
        setIsLoading(true);

        const linksToUpload =
            uploads.value!.filteredMap((upload) => (isLinkToUpload(upload) ? upload : null)) ?? [];

        const postData: RawPost = {
            relation: { projectId },
            userId: user.id,
            title: title.value!,
            text: text!.value!,
            linksToUpload
        };

        await createPost(postData).then(({ postId }) => {
            if (!postId) return;

            const _filesToUpload = uploads.value?.filter(isFileToUpload) as FileToUpload[];

            // const postFilesData = prepareFormDataForUpload(filesToUpload, {
            //     parentId: postId,
            //     collectionRef: StrapiUploadFileRef.Post,
            //     fieldType: StrapiUploadFileField.FilesInPost
            // });

            // if (postFilesData.length > 0)
            //     uploadFiles(postId, RParentFolder.POST_ATTACHMENTS, postFilesData);
        });
    };
    const onUpdate = async () => {
        if (user === undefined || post === undefined) {
            logger.error('Could not update Post. Missing user id or post data!');
            return;
        }

        setIsLoading(true);

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { mediaToUpload, mediaToDelete, mediaToUpdate } = sortMediaUpdates(
            post.uploads ?? [],
            uploads.value!
        );

        const _postData: PostUpdate = {
            title: title.value!,
            text: text.value!,
            // We only want to save links in the post model. The files are uploaded seperately
            linksToUpload: uploads.value!.filter(mixedMediaIsLink)
        };

        // const strapiConfig = {
        //     parentId: post.id,
        //     collectionRef: StrapiUploadFileRef.Post,
        //     fieldType: StrapiUploadFileField.FilesInPost
        // };

        // if (mediaToUpdate.length > 0)
        //     await updateFiles(
        //         mediaToUpdate.map((f) => ({
        //             id: f.id,
        //             data: prepareFormDataForUpdate(f, strapiConfig)
        //         }))
        //     );

        // await updatePost(post.id, postData)
        //     .then(({ postId }) => {
        //         if (!postId) return;

        //         const filesToUpload = mediaToUpload.filter(isFileToUpload);

        //         // // const postFilesData = prepareFormDataForUpload(filesToUpload, strapiConfig);

        //         // // if (postFilesData.length > 0)
        //         // //     uploadFiles(postId, RParentFolder.POST_ATTACHMENTS, postFilesData);
        //     })
        //     .then(() => {
        //         if (mediaToUpdate.length > 0)
        //             updateFiles(
        //                 mediaToUpdate.map((f) => ({
        //                     id: f.id,
        //                     data: prepareFormDataForUpdate(f, strapiConfig)
        //                 }))
        //             );
        //     })
        //     .then(() => {
        //         if (mediaToDelete.length > 0) deleteFiles(mediaToDelete.map((file) => file.id));
        //     });
    };

    const onSubmit = async () => {
        try {
            if (isEditMode) await onUpdate();
            else await onCreate();
            setIsLoading(false);
            onSuccess();
            onClose();
        } catch (_) {
            setIsLoading(false);
            setHasError(true);
        }
    };

    const currentFilesTotalSize = useMemo(
        () =>
            uploads.value!.reduce((acc, file) => {
                const fileSize = getMixedMediaFileSize(file);
                return acc + fileSize;
            }, 0),
        [uploads.value]
    );

    const isSubmitDisabled = useMemo(
        () =>
            title.value!.length === 0 ||
            text.value!.length === 0 ||
            title.hasError ||
            text.hasError,
        [title, text]
    );

    if (isLoading) {
        return (
            <LoadingOverlay
                title={t('post_save_loading_title')}
                text={t('post_save_loading_text')}
            />
        );
    }

    if (hasError) {
        return (
            <ErrorDialog
                error={{
                    title: t('post_save_error_title'),
                    description: t('post_save_error_text')
                }}
                onClose={() => {
                    onClose();
                    setHasError(false);
                }}
                open={hasError}
            />
        );
    }

    return (
        <ModalDialog
            open={!isLoading && !hasError}
            title={isEditMode ? t('edit_post') : t('create_post')}
            onClose={onClose}
            submitText={isEditMode ? t_dialog('save') : t('post_verb')}
            onSubmit={onSubmit}
            isSubmitDisabled={isSubmitDisabled}
            modalContentStyle={{
                gap: '24px',
                padding: '40px 56px'
            }}
        >
            <Column
                gap={2}
                py={1}
                sx={{
                    width: getBreakpointValue(breakpoint, 248, 348, 489),
                    overflowY: 'auto'
                }}
            >
                <DHTextField
                    fullWidth
                    name='title'
                    required
                    label={t('post_title')}
                    placeholder={t('post_title')}
                    maxLength={50}
                    value={title.value}
                    onChange={(e) => setTitle((prev) => ({ ...prev, value: e.target.value }))}
                />
                <DHTextField
                    fullWidth
                    name='text'
                    multiline
                    required
                    label={t('your_post')}
                    placeholder={t('your_post')}
                    rows={5}
                    maxLength={800}
                    value={text.value}
                    onChange={(e) => setText((prev) => ({ ...prev, value: e.target.value }))}
                />

                <FileUpload
                    allowedFileTypes={[FileType.Image, FileType.Document]}
                    withSource
                    withLinkUpload
                    uploads={uploads.value}
                    title={t('add_file')}
                    updateUploads={(value) => setUploads((current) => ({ ...current, value }))}
                    updateHasError={(hasError) =>
                        setUploads((uploads) => ({ ...uploads, hasError }))
                    }
                    currentFilesTotalSize={currentFilesTotalSize}
                />
            </Column>
        </ModalDialog>
    );
};
